import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Share2, 
  Instagram, 
  Facebook, 
  Twitter, 
  Copy, 
  Check, 
  MessageCircle, 
  Mail, 
  Smartphone,
  Image,
  Sparkles,
  Lock,
  ExternalLink,
  Crown
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const CONTENT_TYPES = [
  { id: "availability", label: "I Have Openings!", emoji: "📅", color: "bg-blue-500" },
  { id: "promotion", label: "Special Offer!", emoji: "🎉", color: "bg-green-500" },
  { id: "transformation", label: "Fresh Look Alert!", emoji: "✨", color: "bg-purple-500" },
  { id: "new_service", label: "New Service!", emoji: "🆕", color: "bg-amber-500" },
  { id: "custom", label: "Custom Message", emoji: "✏️", color: "bg-gray-500" }
];

const SocialShareCard = ({ onUpgradeClick }) => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState({});
  const [selectedType, setSelectedType] = useState("availability");
  const [customMessage, setCustomMessage] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [generatedContent, setGeneratedContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  useEffect(() => {
    fetchTemplates();
    checkAccess();
  }, [token]);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/social/templates`);
      setTemplates(res.data.templates || {});
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const checkAccess = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/social/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasAccess(res.data.enabled);
    } catch (error) {
      setHasAccess(false);
    }
  };

  const generateContent = async () => {
    if (!token) {
      toast.error("Please login to use social sharing");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/social/generate`,
        {
          content_type: selectedType,
          title: selectedType === "custom" ? customTitle : null,
          message: selectedType === "custom" ? customMessage : null,
          image_url: imageUrl || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedContent(res.data);
      setShowShareDialog(true);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Upgrade to Pro to use social sharing");
      } else {
        toast.error("Failed to generate content");
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const openShareLink = (platform) => {
    if (generatedContent?.share_links?.[platform]) {
      window.open(generatedContent.share_links[platform], '_blank');
    }
  };

  if (!hasAccess) {
    return (
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader>
          <CardTitle className="font-display text-xl text-white flex items-center gap-2">
            <Share2 className="w-5 h-5 text-amber-500" />
            Social Media Sharing
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Pro</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-500" />
            </div>
            <h3 className="text-white font-semibold mb-2">Upgrade to Pro</h3>
            <p className="text-gray-400 text-sm mb-4">
              Share your availability, promotions, and transformations directly to Instagram, Facebook, Twitter, and more!
            </p>
            <Button 
              onClick={onUpgradeClick}
              className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-semibold"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Pro - $29/mo
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader>
          <CardTitle className="font-display text-xl text-white flex items-center gap-2">
            <Share2 className="w-5 h-5 text-amber-500" />
            Social Media Sharing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Content Type Selection */}
          <div>
            <Label className="text-stone-300 mb-2 block">What do you want to share?</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CONTENT_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    selectedType === type.id
                      ? 'border-amber-500 bg-amber-500/10'
                      : 'border-stone-600 hover:border-stone-500'
                  }`}
                >
                  <span className="text-xl mb-1 block">{type.emoji}</span>
                  <span className="text-sm text-white">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Content Fields */}
          {selectedType === "custom" && (
            <div className="space-y-3">
              <div>
                <Label className="text-stone-300">Title</Label>
                <Input
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="Enter your title..."
                  className="mt-1 bg-stone-700 border-stone-600 text-white"
                />
              </div>
              <div>
                <Label className="text-stone-300">Message</Label>
                <Textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Enter your message..."
                  className="mt-1 bg-stone-700 border-stone-600 text-white"
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Image URL (Optional) */}
          <div>
            <Label className="text-stone-300">Image URL (Optional)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="bg-stone-700 border-stone-600 text-white"
              />
              <Button variant="outline" size="icon" className="border-stone-600">
                <Image className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Generate Button */}
          <Button
            onClick={generateContent}
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-semibold"
          >
            {loading ? (
              <><Sparkles className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Generate Share Content</>
            )}
          </Button>

          {/* Preview of selected template */}
          {selectedType !== "custom" && templates[selectedType] && (
            <div className="p-4 bg-stone-700/50 rounded-lg border border-stone-600">
              <p className="text-xs text-stone-400 mb-1">Preview:</p>
              <p className="text-white font-medium">{templates[selectedType].title}</p>
              <p className="text-stone-300 text-sm mt-1">{templates[selectedType].message}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-amber-500" />
              Share Your Content
            </DialogTitle>
          </DialogHeader>
          
          {generatedContent && (
            <div className="space-y-4">
              {/* Generated Caption */}
              <div className="p-4 bg-stone-700/50 rounded-lg border border-stone-600">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-stone-400">Caption</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(generatedContent.copy_text)}
                    className="text-amber-400 hover:text-amber-300 h-auto p-1"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-white text-sm whitespace-pre-wrap">{generatedContent.message}</p>
              </div>

              {/* Booking Link */}
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <p className="text-xs text-amber-400 mb-1">Your Booking Link</p>
                <div className="flex items-center gap-2">
                  <code className="text-white text-xs flex-1 truncate">{generatedContent.booking_link}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(generatedContent.booking_link)}
                    className="text-amber-400 h-auto p-1"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Share Buttons */}
              <div className="space-y-2">
                <p className="text-sm text-stone-400">Share to:</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => openShareLink('twitter')}
                    className="border-stone-600 text-white hover:bg-[#1DA1F2]/10 hover:border-[#1DA1F2]"
                  >
                    <Twitter className="w-4 h-4 mr-2 text-[#1DA1F2]" />
                    Twitter/X
                    <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => openShareLink('facebook')}
                    className="border-stone-600 text-white hover:bg-[#4267B2]/10 hover:border-[#4267B2]"
                  >
                    <Facebook className="w-4 h-4 mr-2 text-[#4267B2]" />
                    Facebook
                    <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => openShareLink('whatsapp')}
                    className="border-stone-600 text-white hover:bg-[#25D366]/10 hover:border-[#25D366]"
                  >
                    <MessageCircle className="w-4 h-4 mr-2 text-[#25D366]" />
                    WhatsApp
                    <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => openShareLink('email')}
                    className="border-stone-600 text-white hover:bg-stone-700"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                    <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                  </Button>
                </div>

                {/* Instagram Instructions */}
                <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/30 mt-4">
                  <div className="flex items-start gap-3">
                    <Instagram className="w-6 h-6 text-pink-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-medium text-sm">Instagram</p>
                      <p className="text-stone-400 text-xs mt-1">
                        {generatedContent.instagram?.instructions}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(generatedContent.instagram?.caption)}
                        className="mt-2 border-pink-500/30 text-pink-400 hover:bg-pink-500/10"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy Caption for Instagram
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Native Share (Mobile) */}
              {navigator.share && (
                <Button
                  onClick={() => {
                    navigator.share({
                      title: generatedContent.title,
                      text: generatedContent.message,
                      url: generatedContent.booking_link
                    });
                  }}
                  className="w-full bg-stone-700 hover:bg-stone-600 text-white"
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  Share via Device
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SocialShareCard;
